import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import QRCode from 'react-qr-code';
import ThemeResult from '../../components/ThemeResult';
import Modal from '../../components/Modal';
import { mapIcon } from '../../utils/mapIcon';
import LoadingIndicator from '../../components/LoadingIndicator';
import useTrackEvent from '../../utils/trackEvent';
import { usePreferences } from '../../context/PreferencesContext';
import { buildThemeImageMap } from '../../utils/buildThemeImageMap';
import type { AdviceResponse } from '../../types/product';

function Results() {
  const { setResetQuestionnaire } = usePreferences();
  const trackEvent = useTrackEvent();
  const location = useLocation();
  const [results, setResults] = useState<AdviceResponse | null>(null);
  const [didExistanceCheck, setDidExistanceCheck] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [shareLinkModalIsOpen, setShareLinkModalIsOpen] = useState(false);
  const [qrCodeModalIsOpen, setQRCodeModalIsOpen] = useState(false);
  const [linkIsCopied, setLinkIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [themeImageMap, setThemeImageMap] = useState<Record<string, string>>({});
  const [isKuuraAdvice, setIsKuuraAdvice] = useState(false);

  useEffect(() => {
    setThemeImageMap(buildThemeImageMap());
  }, []);

  const resetQuestionnaireState = () => { setResetQuestionnaire(true); };

  useEffect(() => {
    if (results === null || didExistanceCheck) return;
    setDidExistanceCheck(true);

    const newResults = { ...results };

    let checkProductApiUrl: string | false = import.meta.env.VITE_LIBRARY_ENDPOINT ?? '';
    if (window.customer) checkProductApiUrl = window.customer.library_endpoint ?? '';

    try {
      new URL(checkProductApiUrl as string);
    } catch {
      checkProductApiUrl = false;
    }

    const productsList = Object.values(newResults.products);

    if (checkProductApiUrl === false) {
      productsList.forEach((product) => {
        product.exists = false;
        trackEvent('Product_Advised', { select: product.name, id: product.id, uuid: product.uuid, exists_in_library: false });
      });
      setResults({ ...newResults });
    } else {
      axios
        .post(checkProductApiUrl + '/wp-json/wc-uvital/v1/products/exists', productsList.map((p) => p.uuid ?? ''))
        .then((response) => {
          if (response.data) {
            productsList.forEach((product) => {
              const productData = product.uuid ? (response.data[product.uuid] ?? false) : false;
              product.exists = productData;
              trackEvent('Product_Advised', { select: product.name, id: product.id, uuid: product.uuid, exists_in_library: productData });
            });
          }
        })
        .catch(() => {
          productsList.forEach((product) => {
            product.exists = false;
            trackEvent('Product_Advised', { select: product.name, id: product.id, uuid: product.uuid, exists_in_library: false });
          });
        })
        .finally(() => { setResults({ ...newResults }); });
    }
  }, [results]);

  useEffect(() => {
    const getAdvice = async (data: unknown) => {
      setIsLoading(true);
      const baseUrl = import.meta.env.VITE_AZURE_API_URL;
      await axios
        .request({ method: 'post', url: baseUrl + '/api/v1/advice', headers: { 'Content-Type': 'application/json' }, data: JSON.stringify(data) })
        .then((response) => { if (response.data) setResults(response.data); })
        .catch((error) => { setError(error.message); })
        .finally(() => { setIsLoading(false); });
    };

    setShareableLink(window.location.href);
    const queryParams = new URLSearchParams(location.search);
    const themes: { theme: string; advice: { products: string[]; content: string[] } }[] = [];
    let index = 0;

    while (queryParams.has(`theme${index}`)) {
      themes.push({
        theme: queryParams.get(`theme${index}`) ?? '',
        advice: {
          products: queryParams.get(`products${index}`)?.split(',') ?? [],
          content: queryParams.get(`content${index}`)?.split(',') ?? [],
        },
      });
      index++;
    }

    if (themes.length > 0) getAdvice(themes);

    if (themes.length === 1 && themes[0].theme === '' && themes[0].advice.products.length > 0) {
      setIsKuuraAdvice(true);
    }
  }, [location.search]);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shareableLink);
    setLinkIsCopied(true);
    setTimeout(() => { setLinkIsCopied(false); }, 2000);
  };

  return (
    <>
      <div className="bg-main-dark py-6 lg:py-10">
        <div className="site-container">
          <div className="flex flex-wrap justify-between items-center gap-2.5">
            <Link to="/questions" className="inline-flex items-center gap-2.5">
              <i className="fas fa-chevron-left text-secondary-orange"></i>
              <span className="font-bold text-white hover:underline">Terug naar thema's</span>
            </Link>
            <div className="flex flex-wrap items-center gap-2.5">
              <button className="btn btn-secondary" onClick={window.print}>Opslaan als PDF</button>
              <button className="btn btn-secondary" onClick={() => setQRCodeModalIsOpen(!qrCodeModalIsOpen)}>QR code</button>
              <button className="btn btn-secondary" onClick={() => setShareLinkModalIsOpen(!shareLinkModalIsOpen)}>Delen</button>
            </div>
          </div>
          <h1 className="text-white mt-4 lg:mt-2.5">Jouw advies</h1>
          {!isKuuraAdvice && (
            <p className="text-white mt-1 lg:mt-2.5">
              Bedankt voor het invullen van de vragenlijst. Hiermee ben je direct een stap dichterbij de mogelijkheden om langer, zelfstandig thuis te wonen. De mogelijkheden zijn afhankelijk van je persoonlijke situatie, dus bekijk goed waar je precies naar op zoek bent en wat bij jou past. In het resultaat worden productcategorieën getoond. Klik op Bekijk alle producten om alle producten in die categorie te bekijken.
            </p>
          )}
        </div>
      </div>
      <div className="bg-support-grey-100 py-6 lg:py-10">
        <div className="site-container">
          {isKuuraAdvice ? <h2>Hulpmiddelen op basis van je Kuura profiel</h2> : <h2>Jouw thema's</h2>}
          {isLoading && <div className="h-96 flex justify-center items-center"><LoadingIndicator /></div>}
          {results !== null && 'advice' in results && results.advice.length > 0 && !isLoading && !error && (
            <>
              <div className="flex flex-wrap items-center gap-3 mt-1 lg:mt-5">
                {results.advice.map((theme) =>
                  theme.theme !== '' && (
                    <span key={uuidv4()} className="inline-flex items-center space-x-3 border-2 border-main rounded-full py-2 px-6">
                      <img src={`/assets/images/${themeImageMap[theme.theme] || mapIcon(theme.theme)}.svg`} alt={`${theme.theme} icon`} className="h-6 w-6" />
                      <h5>{theme.theme}</h5>
                    </span>
                  )
                )}
              </div>
              {results.advice.map((theme) => (
                <ThemeResult key={uuidv4()} isKuuraAdvice={isKuuraAdvice} theme={theme} adviceResponseObject={results} themeImageMap={themeImageMap} />
              ))}
            </>
          )}
          <div className="px-4 py-5 lg:px-6 lg:py-10 bg-main rounded-lg mt-6 lg:mt-5 max-w-[534px]">
            <h3 className="text-white">Wil je toch iets anders?</h3>
            <p className="text-white text-lg mt-1">Merk je dat je dingen mist? Wil je nog thema's toevoegen?</p>
            <Link to="/" onClick={resetQuestionnaireState} className="btn-sm btn-secondary mt-5">
              {isKuuraAdvice ? 'Doe de Thuisleefwijzer' : 'Doe de Thuisleefwijzer opnieuw'}
            </Link>
          </div>
        </div>
      </div>
      <Modal show={shareLinkModalIsOpen} handleClose={() => setShareLinkModalIsOpen(!shareLinkModalIsOpen)}>
        <div className="p-6">
          <h3>Advies delen</h3>
          <p className="p-6 bg-support-grey-100 rounded-lg mt-6 truncate">{shareableLink}</p>
          <div className="space-x-4 mt-4">
            <a href={`mailto:?subject=Mijn Thuisleefwijzer Resultaat&body=${encodeURIComponent(shareableLink)}`} className="btn btn-primary-outline">
              <i className="fas fa-envelope text-main"></i>
              Link mailen
            </a>
            {linkIsCopied ? (
              <button className="btn btn-primary-outline"><i className="fas fa-check text-main"></i>Link gekopieerd!</button>
            ) : (
              <button className="btn btn-primary-outline" onClick={handleCopyToClipboard}>
                <i className="fas fa-copy text-main"></i>Link kopiëren
              </button>
            )}
          </div>
        </div>
      </Modal>
      <Modal show={qrCodeModalIsOpen} handleClose={() => setQRCodeModalIsOpen(!qrCodeModalIsOpen)}>
        <div className="p-6">
          <h3>Scan de QR-code met de camera van je mobiele telefoon</h3>
          <div className="flex justify-center my-12"><QRCode value={shareableLink} /></div>
        </div>
      </Modal>
    </>
  );
}

export default Results;
